import { useEffect } from "react";
import { useLang } from "@/context/LanguageContext";
import { getKeywords } from "@/i18n/seoKeywords";

const setMeta = (attr, key, value) => {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const setLink = (rel, href, hreflang) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, obj) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
};

export const SEO = ({ title, description, path = "/", image = "/brand/gemprice-logo.png", noindex = false }) => {
  const { lang } = useLang();
  useEffect(() => {
    const site = "https://gemprice.org";
    const url = `${site}${path}`;
    const brandTagline = lang === "fr"
      ? "GEM PRICE — La référence des tarifs de pierres précieuses"
      : "GEM PRICE — The reference for precious gemstone pricing";
    const fullTitle = title ? `${title} — GEM PRICE` : brandTagline;
    const desc =
      description ||
      (lang === "fr"
        ? "GEM PRICE : la référence des tarifs de pierres précieuses. Grille certifiée par pays d'origine et par carat pour saphirs, rubis et émeraudes. Laboratoires GIA, IGI, Gübelin, SSEF, AGL, GRS Gemsearch, tendances du Tucson Gem & Mineral Show et des grands mineral shows."
        : "GEM PRICE: the reference for precious gemstone pricing. Certified grid by country of origin and per carat for sapphires, rubies and emeralds. GIA, IGI, Gübelin, SSEF, AGL, GRS Gemsearch laboratories, Tucson Gem & Mineral Show and major mineral show trends.");
    const keywords = getKeywords(lang, path);

    document.title = fullTitle;
    document.documentElement.setAttribute("lang", lang);

    setMeta("name", "description", desc);
    setMeta("name", "keywords", keywords);
    // Block Google AI Overview from generating snippets from our content.
    // "nosnippet" + "max-snippet:0" prevents inclusion in AI-generated overviews.
    setMeta("name", "robots", noindex
      ? "noindex, nofollow, nosnippet, max-snippet:0"
      : "index, follow, nosnippet, max-snippet:0, max-image-preview:large");
    setMeta("name", "googlebot", noindex
      ? "noindex, nofollow, nosnippet, max-snippet:0"
      : "index, follow, nosnippet, max-snippet:0");
    setMeta("name", "bingbot", noindex
      ? "noindex, nofollow, nosnippet"
      : "index, follow, nosnippet");
    setMeta("name", "author", "GEM PRICE");
    setMeta("name", "theme-color", "#050811");

    // Open Graph
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "GEM PRICE");
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", `${site}${image}`);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:locale", lang === "fr" ? "fr_FR" : "en_US");
    setMeta("property", "og:locale:alternate", lang === "fr" ? "en_US" : "fr_FR");

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", `${site}${image}`);

    // Canonical + hreflang
    setLink("canonical", url);
    setLink("alternate", url, "fr");
    setLink("alternate", url, "en");
    setLink("alternate", url, "x-default");

    // Structured data — Organization (site-wide identity for Google Knowledge Graph)
    setJsonLd("ld-organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${site}/#organization`,
      name: "GEM PRICE",
      alternateName: ["GemPrice", "Gem Price", "gemprice.org"],
      legalName: "GEM PRICE",
      url: site,
      logo: {
        "@type": "ImageObject",
        url: `${site}/brand/gemprice-logo.png`,
        width: 512,
        height: 512,
      },
      image: `${site}/brand/gemprice-logo.png`,
      email: "gemprice@gemprice.org",
      slogan: lang === "fr"
        ? "La référence des tarifs de pierres précieuses."
        : "The reference for precious gemstone prices.",
      description:
        lang === "fr"
          ? "GEM PRICE — La référence des tarifs de pierres précieuses par pays d'origine et par carat."
          : "GEM PRICE — The reference for precious gemstone pricing by country of origin and per carat.",
      knowsAbout: [
        "Gemmology", "Gemology", "Precious gemstones", "Gemstone pricing",
        "Sapphire", "Ruby", "Emerald", "Saphir", "Rubis", "Émeraude",
        "Corundum", "Beryl", "Fine gems",
        "GIA — Gemological Institute of America",
        "IGI — International Gemological Institute",
        "SSEF — Swiss Gemmological Institute",
        "Gübelin Gem Lab",
        "AGL — American Gemological Laboratories",
        "GRS — Gemresearch Swisslab", "GRS Gemsearch",
        "Lotus Gemology", "AIGS Laboratory",
        "Tucson Gem Show", "Tucson Gem & Mineral Show",
        "AGTA GemFair Tucson", "Mineral shows",
        "Origin determination", "Country of origin certification",
        "Gemstone treatments", "Heated sapphires", "Oiled emeralds",
        "Synthetic gemstones", "Ceylon sapphire", "Kashmir sapphire",
        "Burmese ruby", "Colombian emerald", "Zambian emerald",
        "Padparadscha sapphire", "Colour-change sapphire",
      ],
      areaServed: "Worldwide",
      sameAs: [],
      contactPoint: [{
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "gemprice@gemprice.org",
        availableLanguage: ["French", "English"],
      }],
    });

    // Structured data — WebSite (enables sitelinks search box + Organization link)
    setJsonLd("ld-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${site}/#website`,
      name: "GEM PRICE",
      alternateName: "gemprice.org",
      url: site,
      inLanguage: lang === "fr" ? "fr-FR" : "en-US",
      description: desc,
      publisher: { "@id": `${site}/#organization` },
      copyrightHolder: { "@id": `${site}/#organization` },
      copyrightYear: 2026,
    });

    // Structured data — Product (only on Home) so the €79 offer is eligible for rich results
    if (path === "/") {
      setJsonLd("ld-product", {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "GEM PRICE — Premium Subscription",
        description:
          lang === "fr"
            ? "Accès complet aux tarifications certifiées des pierres précieuses par pays et par carat, aux fiches laboratoires et au journal mensuel."
            : "Full access to certified gemstone pricing by country and per carat, laboratory sheets and monthly journal.",
        brand: { "@type": "Brand", name: "GEM PRICE" },
        offers: {
          "@type": "Offer",
          price: "79.00",
          priceCurrency: "EUR",
          availability: "https://schema.org/InStock",
          url: site,
        },
      });
    } else {
      const el = document.getElementById("ld-product");
      if (el) el.remove();
    }

    // Structured data — Breadcrumbs on inner pages (helps rich results)
    if (path && path !== "/") {
      const parts = path.split("/").filter(Boolean);
      const items = [{
        "@type": "ListItem",
        position: 1,
        name: lang === "fr" ? "Accueil" : "Home",
        item: site,
      }];
      parts.forEach((p, i) => {
        items.push({
          "@type": "ListItem",
          position: i + 2,
          name: p.charAt(0).toUpperCase() + p.slice(1),
          item: `${site}/${parts.slice(0, i + 1).join("/")}`,
        });
      });
      setJsonLd("ld-breadcrumbs", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items,
      });
    } else {
      const el = document.getElementById("ld-breadcrumbs");
      if (el) el.remove();
    }
  }, [title, description, path, image, lang, noindex]);
  return null;
};

export default SEO;
